<script>
import axios from 'axios';
import Promotion from '@/models/promotion';
import DateTime from '@/helpers/date';
import { PromotionsController } from '@/controllers/promotions';
import { GlobalPolicy } from '@/policies/global';
import ModalDialog from '@/components/ModalDialog.vue';
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, SelectField, TextInput, URLField, UploadFiles, SearchInput } from '@/components/form';
import CheckboxInput from '@/components/form/CheckboxInput.vue';
import PaginationTable from '@/components/PaginationTable.vue';

export default {
    setup() {
        return { GlobalPolicy, Promotion };
    },
    props: {
        context: {
            type: String,
            required: true
        },
        id: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            promotions: [],
            allPromotions: [],
            selected: [],
            query: { search: '' },
            bulkModal: false,
            createModal: false,
            saving: false,
            newPromotion: new Promotion()
        };
    },
    computed: {
        start_at: {
            get() { return DateTime.toDateTimeInput(this.newPromotion._start_at); },
            set(value) { this.newPromotion.start_at = value; }
        },
        end_at: {
            get() { return DateTime.toDateTimeInput(this.newPromotion._end_at); },
            set(value) { this.newPromotion.end_at = value; }
        },
        available() {
            const attachedIds = this.promotions.map(p => p.id);
            let results = this.allPromotions.filter(p => !attachedIds.includes(p.id));
            if (this.query.search) {
                const term = this.query.search.toLowerCase();
                results = results.filter(p =>
                    (p.headline || '').toLowerCase().includes(term) ||
                    (p.description || '').toLowerCase().includes(term)
                );
            }
            return results;
        },
        createValid() {
            const hasLargeImage = this.newPromotion.meta?.large_image_url || this.newPromotion.meta?.large_image;
            const hasSmallImage = this.newPromotion.meta?.small_image_url || this.newPromotion.meta?.small_image;
            return this.newPromotion.headline?.length > 0 &&
                this.newPromotion.button_text?.length > 0 &&
                this.newPromotion.description?.length > 0 &&
                this.newPromotion.type != null &&
                !!hasLargeImage &&
                this.newPromotion.meta?.large_image_alt?.length > 0 &&
                (!hasSmallImage || this.newPromotion.meta?.small_image_alt?.length > 0);
        },
        small_image: {
            get() {
                if (this.newPromotion.meta?.small_image_url) return [this.newPromotion.meta.small_image_url];
                if (this.newPromotion.meta?.small_image) return [this.newPromotion.meta.small_image];
                return [];
            },
            set(value) {
                this.newPromotion.meta.small_image_url = null;
                this.newPromotion.meta.small_image = value[0] ?? null;
            }
        },
        large_image: {
            get() {
                if (this.newPromotion.meta?.large_image_url) return [this.newPromotion.meta.large_image_url];
                if (this.newPromotion.meta?.large_image) return [this.newPromotion.meta.large_image];
                return [];
            },
            set(value) {
                this.newPromotion.meta.large_image_url = null;
                this.newPromotion.meta.large_image = value[0] ?? null;
            }
        },
        asset_file: {
            get() {
                if (this.newPromotion.file) return [this.newPromotion.file];
                return [];
            },
            set(value) {
                this.newPromotion.file = value[0] ?? null;
            }
        },
        mainColumns() {
            return [
                { name: 'headline', label: 'Headline', default: true, sort: (item) => item.headline || '' },
                { name: 'type', label: 'Type', default: true, sort: (item) => item.type || '' },
                ...(GlobalPolicy.edit() ? [{ name: 'actions', label: '', default: true }] : [])
            ];
        },
        bulkColumns() {
            return [
                { name: 'select', label: '', sort: false, default: true },
                { name: 'headline', label: 'Headline', sort: (item) => item.headline || '', default: true },
                { name: 'type', label: 'Type', sort: (item) => item.type || '', default: true }
            ];
        }
    },
    methods: {
        index() {
            axios.get(`${this.context}/${this.id}/promotions`)
                .then(resp => {
                    this.promotions = resp.data.map(p => new Promotion(p));
                })
                .catch(err => {
                    console.error(err);
                });
        },
        openBulk() {
            PromotionsController.index()
                .then(promotions => {
                    this.allPromotions = promotions;
                    this.bulkModal = true;
                })
                .catch(err => {
                    console.error(err);
                });
        },
        attach() {
            if (!this.selected.length) return;
            axios.post(`${this.context}/${this.id}/promotions/attach`, { items: this.selected })
                .then(resp => {
                    this.promotions = resp.data.map(p => new Promotion(p));
                    this.cancel();
                })
                .catch(err => {
                    console.error(err);
                });
        },
        unsync(promotion) {
            axios.delete(`${this.context}/${this.id}/promotions/${promotion.id}`)
                .then(resp => {
                    this.promotions = resp.data.map(p => new Promotion(p));
                })
                .catch(err => {
                    console.error(err);
                });
        },
        openCreate() {
            this.newPromotion = new Promotion();
            this.createModal = true;
        },
        create() {
            if (!this.createValid) return;
            this.saving = true;
            const payload = {
                headline: this.newPromotion.headline,
                type: this.newPromotion.type,
                button_text: this.newPromotion.button_text,
                open_in_new_tab: this.newPromotion.open_in_new_tab,
                description: this.newPromotion.description,
                disclaimer: this.newPromotion.disclaimer,
                start_at: this.newPromotion.start_at,
                end_at: this.newPromotion.end_at,
                url: this.newPromotion.url,
                file: this.newPromotion.file,
                meta: this.newPromotion.meta
            };
            axios.postForm('promotions', payload)
                .then(resp => {
                    return axios.post(`${this.context}/${this.id}/promotions/attach`, { items: [resp.data.id] });
                })
                .then(resp => {
                    this.promotions = resp.data.map(p => new Promotion(p));
                    this.cancel();
                })
                .catch(err => {
                    console.error(err);
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        cancel() {
            this.bulkModal = false;
            this.createModal = false;
            this.selected = [];
            this.query = { search: '' };
            this.newPromotion = new Promotion();
        },
        toDateInput(value) {
            if (!value) return null;
            const d = new Date(value);
            if (isNaN(d)) return null;
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        }
    },
    created() {
        this.index();
    },
    components: {
        ModalDialog,
        LoadingView,
        FormButton,
        TextInput,
        URLField,
        UploadFiles,
        SelectField,
        SearchInput,
        CheckboxInput,
        PaginationTable
    }
}
</script>

<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased">Promotions</h2>
        <nav v-if="GlobalPolicy.edit()" class="flex items-end gap-2">
            <FormButton @click="openBulk" icon="fa-link" icon-size="text-lg" prefix-class="fak" color="blue">Bulk Attach</FormButton>
            <FormButton @click="openCreate" icon="fa-plus" icon-size="text-lg" prefix-class="fak" color="blue">Create Promotion</FormButton>
        </nav>
    </div>

    <PaginationTable
        empty-message="There are currently no promotions attached."
        :legacy="false"
        :columns="mainColumns"
        view="promotions-pane"
        :data="promotions">
        <template #col-headline="{row}">
            <div class="block py-4 whitespace-nowrap">
                <router-link
                    :to="{ name: 'promotion-show', params: { id: row.id } }"
                    class="text-sm leading-5 font-medium text-bhav-blue-500 hover:underline">
                    {{ row.headline }}
                </router-link>
            </div>
        </template>
        <template #col-type="{row}">
            <div class="py-4 whitespace-nowrap">
                <span class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl bg-bhav-cool-gray-100 text-bhav-cool-gray-700">
                    {{ row.type }}
                </span>
            </div>
        </template>
        <template #col-actions="{row}">
            <div class="py-4 flex justify-end">
                <FormButton type="button" @click="unsync(row)" title="Detach this promotion" prefix-class="fak" icon="fa-detach" color="red">
                    <span class="sr-only">Detach</span>
                </FormButton>
            </div>
        </template>
    </PaginationTable>

    <!-- Bulk Attach Modal -->
    <ModalDialog :no-padding="true" :open="bulkModal" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-500 font-semibold">Bulk Attach Promotions</h2>
        </template>
        <template #body>
            <div class="mb-4">
                <SearchInput v-model="query.search" placeholder="Search promotions..." />
            </div>
            <PaginationTable
                empty-message="No unattached promotions available."
                :legacy="false"
                :columns="bulkColumns"
                view="promotions-pane-bulk"
                :data="available">
                <template #col-select="{row}">
                    <CheckboxInput :showLabel="false" :label="row.headline || ''" v-model="selected" :value="row.id" />
                </template>
                <template #col-headline="{row}">
                    <div class="py-2 text-sm text-bhav-blue-500">{{ row.headline }}</div>
                </template>
                <template #col-type="{row}">
                    <div class="py-2 whitespace-nowrap">
                        <span class="px-3 h-7 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl bg-bhav-cool-gray-100 text-bhav-cool-gray-700">
                            {{ row.type }}
                        </span>
                    </div>
                </template>
            </PaginationTable>
        </template>
        <template #buttons>
            <FormButton @click="cancel" color="gray">Cancel</FormButton>
            <FormButton @click="attach" :disabled="!selected.length" color="blue">Attach Selected</FormButton>
        </template>
    </ModalDialog>

    <Teleport to="body">
        <LoadingView v-if="saving" class="!z-[200]" />
    </Teleport>

    <!-- Create Promotion Modal -->
    <ModalDialog :no-padding="true" :open="createModal" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-500 font-semibold">Create Promotion</h2>
        </template>
        <template #body>
            <form @submit.prevent="create" class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4">
                    <legend class="sr-only">Promotion Properties</legend>

                    <label for="new.headline" class="font-normal text-bhav-blue-300 py-5">
                        Headline <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5">
                        <TextInput id="new.headline" v-model="newPromotion.headline" placeholder="Promotion headline" required maxlength="255" class="max-w-80" />
                    </div>

                    <label for="new.type" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Type <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <SelectField id="new.type" v-model="newPromotion.type" :options="Promotion.types" :allow-null="false" class="max-w-80" />
                    </div>

                    <label for="new.description" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Description <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <textarea class="max-w-md bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-32" id="new.description" v-model="newPromotion.description" required maxlength="1000"></textarea>
                    </div>

                    <label for="new.button_text" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Button Text <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <TextInput id="new.button_text" v-model="newPromotion.button_text" placeholder="Button Text" required maxlength="255" class="max-w-80" />
                    </div>

                    <label for="new.open_in_new_tab" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Open in New Tab
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <ToggleButton label="Open in New Tab" :checked="newPromotion.open_in_new_tab" @changed="newPromotion.open_in_new_tab = $event" />
                    </div>

                    <label for="new.disclaimer" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Disclaimer
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <textarea class="max-w-md bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-32" id="new.disclaimer" v-model="newPromotion.disclaimer" required maxlength="1000"></textarea>
                    </div>

                    <label for="new.start_at" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Start Date
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <TextInput id="new.start_at" type="datetime-local" v-model="start_at" class="max-w-80" />
                    </div>

                    <label for="new.end_at" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        End Date
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <TextInput id="new.end_at" type="datetime-local" v-model="end_at" class="max-w-80" />
                    </div>

                    <label for="new.url" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        URL
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <URLField id="new.url" v-model="newPromotion.url" class="max-w-80" />
                    </div>

                    <label for="new.file" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        File
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <UploadFiles v-model="asset_file" :multiple="false" format="file" :accept="['application/pdf']" />
                    </div>

                    <label class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Small Image
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <UploadFiles v-model="small_image" :multiple="false" />
                        <TextInput v-model="newPromotion.meta.small_image_alt" placeholder="Alt text" class="max-w-80 mt-2" />
                    </div>

                    <label class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Large Image <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <UploadFiles v-model="large_image" :multiple="false" />
                        <TextInput v-model="newPromotion.meta.large_image_alt" placeholder="Alt text" class="max-w-80 mt-2" />
                    </div>

                    <label for="new.meta.show_image_inline" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        Show image inline on page
                    </label>
                    <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
                        <ToggleButton label="Show image inline on page" :checked="newPromotion.meta.show_image_inline" @changed="newPromotion.meta.show_image_inline = $event" />
                    </div>
                </form>
        </template>
        <template #buttons>
            <FormButton @click="cancel" color="gray">Cancel</FormButton>
            <FormButton @click="create" :disabled="saving || !createValid" color="blue">{{ saving ? 'Saving...' : 'Save' }}</FormButton>
        </template>
    </ModalDialog>
</template>
